import { useState, useEffect } from 'react';
import { Swords } from 'lucide-react';
import { StoryStep } from './StoryStep';
import { fmtMoney } from './story-utils';
import type { CompetitorAudit, PageAudit } from '@/types/sem';
import { supabase } from '@/lib/supabase';

interface CompetitorStepProps {
  audit: CompetitorAudit;
  pageAudit?: PageAudit | null;
}

function getValueColor(gapCost: number): string {
  if (gapCost <= -1000) return 'competitor-val-brick';
  if (gapCost < 0) return 'competitor-val-brass';
  return 'competitor-val-sage';
}

function ScreenshotImage({ path, label }: { path: string | null; label: string }) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!path || !supabase) return;
    const { data } = supabase.storage.from('audit-screenshots').getPublicUrl(path);
    if (data?.publicUrl) setUrl(data.publicUrl);
  }, [path]);

  if (!path) return null;

  return (
    <div className="competitor-screenshot-side">
      <div className="competitor-screenshot-label">{label}</div>
      <div className="competitor-screenshot-wrap">
        {url ? (
          <img src={url} alt={label} loading="lazy" />
        ) : (
          <div className="competitor-screenshot-placeholder">Loading…</div>
        )}
      </div>
    </div>
  );
}

export function CompetitorStep({ audit, pageAudit }: CompetitorStepProps) {
  const yourScore = audit.your_score ?? 0;
  const compScore = audit.competitor_score ?? 0;
  const gap = compScore - yourScore;
  const totalGapCost = audit.total_gap_cost ?? 0;
  const hasScreenshots = !!(audit.competitor_screenshot_path || pageAudit?.screenshot_path);

  return (
    <StoryStep
      label="Competitive Gap"
      tag={
        <div className={`story-tg ${gap > 0 ? 'story-tg-warn' : 'story-tg-ok'}`}>
          <Swords size={10} />
          {gap > 0 ? `−${gap}pts` : 'Ahead'}
        </div>
      }
      collapsedStats={
        <div className="story-acc-stat">
          <span className="label">"{audit.keyword}"</span>
          <span className={totalGapCost < 0 ? 'story-val-red' : 'story-val-green'}>
            {totalGapCost < 0 ? '−' : '+'}{fmtMoney(Math.abs(totalGapCost))}/mo
          </span>
        </div>
      }
    >
      {/* Score comparison */}
      <div className="competitor-scores" style={{ padding: '0 0 16px' }}>
        <div className="competitor-score-side">
          <div className="competitor-score-label">Your Page</div>
          <div className="competitor-score-num">
            <span className="competitor-score-value">{yourScore}</span>
            <span className="competitor-score-max">/100</span>
          </div>
          <div className="competitor-score-bar">
            <div
              className={`competitor-score-fill ${yourScore < 50 ? 'competitor-score-fill-low' : 'competitor-score-fill-high'}`}
              style={{ width: `${yourScore}%` }}
            />
          </div>
        </div>

        <div className="competitor-vs">VS</div>

        <div className="competitor-score-side competitor-right">
          <div className="competitor-score-label">{audit.competitor_domain || 'Top Competitor'}</div>
          <div className="competitor-score-num">
            <span className="competitor-score-value">{compScore}</span>
            <span className="competitor-score-max">/100</span>
          </div>
          <div className="competitor-score-bar">
            <div
              className={`competitor-score-fill ${compScore < 50 ? 'competitor-score-fill-low' : 'competitor-score-fill-high'}`}
              style={{ width: `${compScore}%` }}
            />
          </div>
        </div>
      </div>

      {/* Side-by-side screenshots */}
      {hasScreenshots && (
        <div style={{ paddingBottom: 12, borderBottom: '1px solid rgba(255,255,255,0.04)', marginBottom: 12 }}>
          <div className="competitor-screenshots-grid" style={{ padding: 0 }}>
            <ScreenshotImage
              path={pageAudit?.screenshot_path ?? null}
              label="Your Page"
            />
            <ScreenshotImage
              path={audit.competitor_screenshot_path}
              label={`Competitor · ${audit.competitor_domain}`}
            />
          </div>
        </div>
      )}

      {/* Element comparison cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {audit.element_comparison.map((row, i) => {
          const colorClass = getValueColor(row.gap_cost);
          return (
            <div className="comp-element-card" key={`${row.element}-${i}`} style={{ margin: 0 }}>
              <div className="comp-element-header">
                <span className="comp-element-name">{row.element}</span>
                <span className={`comp-element-cost ${colorClass}`}>
                  {row.gap_cost < 0 ? '−' : '+'}
                  {fmtMoney(Math.abs(row.gap_cost))}
                </span>
              </div>
              <div className="comp-element-panels">
                <div className="comp-element-panel comp-panel-you">
                  <div className="comp-panel-label">You</div>
                  <div className={`comp-panel-text ${getValueColor(row.gap_cost)}`}>
                    {row.yours}
                  </div>
                </div>
                <div className="comp-element-panel comp-panel-them">
                  <div className="comp-panel-label">Competitor</div>
                  <div className={`comp-panel-text ${row.gap_cost < 0 ? 'competitor-val-sage' : 'competitor-val-brick'}`}>
                    {row.theirs}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* AI Analysis */}
      {audit.ai_analysis && (
        <div style={{ paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.04)', marginTop: 16 }}>
          <div className="competitor-analysis-label">Analysis</div>
          <div className="competitor-analysis-text">
            {audit.ai_analysis.split(/(\$[\d,]+\/mo)/g).map((part, i) =>
              part.match(/^\$[\d,]+\/mo$/) ? (
                <span key={i} className="competitor-analysis-recovery">{part}</span>
              ) : (
                <span key={i}>{part}</span>
              )
            )}
          </div>
        </div>
      )}
    </StoryStep>
  );
}
